import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Delete,
  Edit,
  CloudUpload,
  Folder,
  Description,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE } from '../config';

function AdminDocumentation() {
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [documentDialog, setDocumentDialog] = useState(false);
  const [editDocDialog, setEditDocDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ id: '', label: '', color: 'primary' });
  const [newDocument, setNewDocument] = useState({ title: '', description: '', category: '', content: '', file: null });
  const [editDocument, setEditDocument] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [uploadError, setUploadError] = useState('');

  const showAlert = useCallback((message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const categoriesResponse = await axios.get(`${API_BASE}/documentation-categories`);
      setCategories(categoriesResponse.data || []);
      const documentsResponse = await axios.get(`${API_BASE}/documentation-documents`);
      setDocuments(documentsResponse.data || []);
    } catch (error) {
      showAlert('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateCategory = async () => {
    if (!newCategory.id || !newCategory.label) {
      showAlert('Please fill in all category fields', 'warning');
      return;
    }
    try {
      await axios.post(
        `${API_BASE}/categories-documents/category`,
        newCategory,
        { headers: { 'Content-Type': 'application/json' } }
      );
      showAlert('Category created successfully');
      setNewCategory({ id: '', label: '', color: 'primary' });
      setCategoryDialog(false);
      fetchData();
    } catch (error) {
      showAlert('Failed to create category', 'error');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`${API_BASE}/categories-documents/category/${categoryId}`);
      showAlert('Category deleted successfully');
      fetchData();
    } catch (error) {
      showAlert('Failed to delete category', 'error');
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewDocument({
        ...newDocument,
        file: file,
        title: newDocument.title || file.name.replace(/\.[^/.]+$/, '')
      });
    }
  };

  const handleCreateDocument = async () => {
    setUploadError('');
    if (!newDocument.title) {
      setUploadError('Document title is required');
      return;
    }
    if (!newDocument.category) {
      setUploadError('Please select a category');
      return;
    }
    if (!newDocument.file) {
      setUploadError('Please select a file');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('title', newDocument.title);
      formData.append('description', newDocument.description);
      formData.append('category', newDocument.category);
      await axios.post(`${API_BASE}/categories-documents/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showAlert('Document uploaded successfully');
      setNewDocument({ title: '', description: '', category: '', content: '', file: null });
      setUploadError('');
      setDocumentDialog(false);
      fetchData();
    } catch (error) {
      setUploadError('Failed to upload document: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API_BASE}/categories-documents/document/${docId}`);
      showAlert('Document deleted successfully');
      fetchData();
    } catch (error) {
      showAlert('Failed to delete document', 'error');
    }
  };

  const openEditDialog = (doc) => {
    setEditDocument({ ...doc });
    setEditDocDialog(true);
  };

  const handleUpdateDocument = async () => {
    if (!editDocument.title) {
      showAlert('Please fill in the title', 'warning');
      return;
    }
    try {
      await axios.put(`${API_BASE}/categories-documents/document/${editDocument.id}`, {
        title: editDocument.title,
        description: editDocument.description,
        category: editDocument.category,
      });
      showAlert('Document updated successfully');
      setEditDocDialog(false);
      setEditDocument(null);
      fetchData();
    } catch (error) {
      showAlert('Failed to update document', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          Admin Documentation Manager
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Folder />}
            onClick={() => setCategoryDialog(true)}
          >
            Add Category
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CloudUpload />}
            onClick={() => { setUploadError(''); setDocumentDialog(true); }}
          >
            Upload Document
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Paper sx={{ flex: 1, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Folder /> Categories ({categories.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No categories yet. Create one to get started.
            </Typography>
          ) : (
            <List>
              {categories.map((cat) => {
                const categoryDocs = documents.filter(d => d.category === cat.id);
                const isSelected = selectedCategory === cat.id;
                
                return (
                  <React.Fragment key={cat.id}>
                    <ListItem
                      sx={{ 
                        bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5' },
                        borderLeft: isSelected ? '4px solid #1976d2' : '4px solid transparent'
                      }}
                      onClick={() => handleCategoryClick(cat.id)}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(cat.id);
                          }} 
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={cat.label} color={cat.color || 'default'} size="small" />
                            <Typography variant="body2" color="text.secondary">
                              ({cat.id})
                            </Typography>
                          </Box>
                        }
                        secondary={`${categoryDocs.length} documents`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>

        {/* Documents Section - Right Side - Shows selected category's documents */}
        <Paper sx={{ flex: 2, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description /> 
              {selectedCategory 
                ? `${categories.find(c => c.id === selectedCategory)?.label || 'Category'} Documents`
                : 'Select a Category'}
            </Typography>
          </Box>
          {selectedCategory && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search documents by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          {!selectedCategory ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Folder sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Select a category from the left to view its documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click on any category to manage its documents
              </Typography>
            </Box>
          ) : (
            <>
              {documents.filter(d => d.category === selectedCategory).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Description sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No documents in this category yet
                  </Typography>
                </Box>
              ) : (
                <List>
                  {documents
                    .filter(d => {
                      const matchesCategory = d.category === selectedCategory;
                      if (!searchQuery) return matchesCategory;
                      const query = searchQuery.toLowerCase();
                      return matchesCategory && (
                        d.title?.toLowerCase().includes(query) ||
                        d.description?.toLowerCase().includes(query)
                      );
                    })
                    .map((doc) => {
                      const category = categories.find(c => c.id === doc.category);
                      return (
                        <React.Fragment key={doc.id}>
                          <ListItem
                            secondaryAction={
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton edge="end" onClick={() => openEditDialog(doc)} color="primary">
                                  <Edit />
                                </IconButton>
                                <IconButton edge="end" onClick={() => handleDeleteDocument(doc.id)} color="error">
                                  <Delete />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {doc.title}
                                  </Typography>
                                  {category && (
                                    <Chip label={category.label} color={category.color} size="small" />
                                  )}
                                </Box>
                              }
                              secondary={doc.description}
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      );
                    })}
                </List>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* Create Category Dialog */}
      <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category ID"
            fullWidth
            margin="normal"
            value={newCategory.id}
            onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })}
            helperText="Unique identifier (e.g., api_docs, guides)"
          />
          <TextField
            label="Category Label"
            fullWidth
            margin="normal"
            value={newCategory.label}
            onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
            helperText="Display name for the category"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Color</InputLabel>
            <Select
              value={newCategory.color}
              onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
            >
              <MenuItem value="primary">Primary (Blue)</MenuItem>
              <MenuItem value="secondary">Secondary (Purple)</MenuItem>
              <MenuItem value="success">Success (Green)</MenuItem>
              <MenuItem value="error">Error (Red)</MenuItem>
              <MenuItem value="warning">Warning (Orange)</MenuItem>
              <MenuItem value="info">Info (Light Blue)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={documentDialog} onClose={() => { setDocumentDialog(false); setUploadError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
          <FormControl fullWidth margin="normal">
            <InputLabel>Category *</InputLabel>
            <Select
              value={newDocument.category}
              onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUpload />}
            sx={{ mt: 2, mb: 2 }}
          >
            Choose File
            <input type="file" hidden accept=".txt,.md,.json,.html,.pdf" onChange={handleFileUpload} />
          </Button>

          {newDocument.file && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ✓ File selected: {newDocument.file.name}
            </Alert>
          )}

          <TextField
            label="Document Title *"
            fullWidth
            margin="normal"
            value={newDocument.title}
            onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={newDocument.description}
            onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDocumentDialog(false); setUploadError(''); }}>Cancel</Button>
          <Button onClick={handleCreateDocument} variant="contained" color="success">
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocDialog} onClose={() => setEditDocDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          {editDocument && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category *</InputLabel>
                <Select
                  value={editDocument.category}
                  onChange={(e) => setEditDocument({ ...editDocument, category: e.target.value })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Document Title *"
                fullWidth
                margin="normal"
                value={editDocument.title}
                onChange={(e) => setEditDocument({ ...editDocument, title: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                multiline
                rows={2}
                value={editDocument.description}
                onChange={(e) => setEditDocument({ ...editDocument, description: e.target.value })}
              />
              <TextField
                label="Content"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={editDocument.content || ''}
                disabled
                helperText="PDF content cannot be edited. Delete and re-upload to change the file."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDocDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateDocument} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminDocumentation;
